import { useCallback } from "react";

import { useFeature } from "byzantine";
import { useLocalization } from "@fluent/react";

import { canShowPaymentActivity } from "../../../../components/PaymentActivity/featureGuards";
import { PAYMENT_ACTIVITY_ROUTE } from "../../../../components/PaymentActivity/routes";
import {
  areTemplatesAvailable,
  TEMPLATES_ROUTE,
} from "../../../../components/Templates/constants";

import type { StandardOptionItem, SidebarRootItem } from "../sidebar.types";

/**
 * Hook that returns a builder for the manage-payments menu item.
 *
 * Internalizes `useLocalization` and `useFeature` so the caller
 * doesn't need to manage these dependencies.
 *
 * Responsibilities:
 * 1. Converts backend subItems into StandardOptionItem[]
 * 2. Prepends Payment Activity if the feature is enabled
 * 3. Inserts Templates after Payment Activity (or at end)
 */
export const useManagePaymentsMenuItem = () => {
  const { l10n } = useLocalization();
  const { features } = useFeature();

  return useCallback(
    (menuItem: API.SidebarMenuItem_Resolved): SidebarRootItem => {
      const existingMenuOptions: StandardOptionItem[] = (
        menuItem.subItems || []
      )
        .filter((subItem) => Boolean(subItem.route))
        .map((subItem) => ({
          type: "standard" as const,
          title: subItem.title,
          route: subItem.route,
          icon: subItem.icon,
          newTab: subItem.newTab,
        }));

      const menuOptions = [...existingMenuOptions];

      const paymentActivityRoute = `/${PAYMENT_ACTIVITY_ROUTE}`;
      const shouldShowPaymentActivity = canShowPaymentActivity(features);
      const paymentActivityIndex = menuOptions.findIndex(
        (option) => option.route === paymentActivityRoute
      );

      if (shouldShowPaymentActivity && paymentActivityIndex < 0) {
        menuOptions.unshift({
          type: "standard",
          title: l10n.getString("sidebar-payment-activity-label"),
          route: paymentActivityRoute,
        });
      }

      const updatedPaymentActivityIndex = menuOptions.findIndex(
        (option) => option.route === paymentActivityRoute
      );

      if (areTemplatesAvailable(features)) {
        const templatesInsertIndex =
          updatedPaymentActivityIndex >= 0
            ? updatedPaymentActivityIndex + 1
            : menuOptions.length;

        menuOptions.splice(templatesInsertIndex, 0, {
          type: "standard",
          title: l10n.getString("sidebar-menu-templates"),
          route: `/${TEMPLATES_ROUTE}`,
        });
      }

      return {
        ...menuItem,
        menuOptions,
      };
    },
    [l10n, features]
  );
};
